import {
  save,
  findAll,
  findById,
  removeGroup,
  usersInactive,
  getGroupsAccess,
} from '@/service/groups'
import { breadcrumb } from '@/helpers/breadcrumb'

// helper function
const asyncForEach = async (array, callback) => {
  for (let index = 0; index < array.length; index++) {
    await callback(array[index], index, array)
  }
}

const importGroups = function() {
  return {
    groupsTree: [],
    groups: [],
    loading: false,
    loadingMessage: null,
    loadingErrors: [],
  }
}

const cleanGroupData = function() {
  return {
    name: '',
    managers: [],
    members: [],
    children: [],
    active: true, // @TODO mudar para false
  }
}

function initialState() {
  return {
    groups: {
      modal: {
        data: cleanGroupData(),
        errors: {},
        submitInProgress: false,
      },
      data: {
        breadcrumb: breadcrumb.get(),
        currentGroup: {},
        groupList: [],
      },
      groupToRemove: {},
      groupInactiveUsers: {},
    },
    import: importGroups(),
  }
}

const state = initialState()

const getters = {
  modal: state => state.groups.modal,
  modalGroup: state => state.groups.modal.data,
  currentGroup: state => state.groups.data.currentGroup,
  groupList: state => state.groups.data.groupList,
  breadcrumb: state => state.groups.data.breadcrumb,
  groupsTree: state => state.import.groupsTree,
  groups: state => state.import.groups,
  loading: state => state.import.loading,
  loadingMessage: state => state.import.loadingMessage,
  loadingErrors: state => state.import.loadingErrors,
}

const mutations = {
  MODAL_ERROR: (state, error) => {
    state.groups.modal.errors = Object.assign({}, error)
  },

  SET_CURRENT_GROUP: (state, group) => {
    state.groups.data.groupList = group.children
    state.groups.data.currentGroup = group._id ? group : {}
    state.groups.data.breadcrumb = breadcrumb.get()
  },

  UPDATE_BREADCRUMB: state => {
    state.groups.data.breadcrumb = breadcrumb.get()
  },

  // eslint-disable-next-line no-unused-vars
  RESET_STATE: state => {
    state = initialState()
  },

  RESET_MODAL: state => {
    state.groups.modal.data = cleanGroupData()
  },

  SET_GROUP_TO_EDIT: (state, group) => {
    state.groups.modal.data = group
  },

  SET_GROUP_TO_REMOVE: (state, group) => {
    state.groups.groupToRemove = group
  },

  SET_GROUP_INACTIVE_USERS: (state, group) => {
    state.groups.groupInactiveUsers = group
  },

  SET_SUBMIT_PROGRESS: (state, isInProgress) => {
    state.groups.modal.submitInProgress = isInProgress
  },

  SET_IMPORT_FORM: (state, form) => {
    state.import = Object.assign(importGroups(), form)
  },

  SET_IMPORT_LOADING: (state, loading) => {
    state.import.loading = loading
  },

  SET_IMPORT_LOADING_MESSAGE: (state, message) => {
    state.import.loadingMessage = message
  },

  ADD_IMPORT_LOADING_ERROR: (state, message) => {
    state.import.loadingErrors.push(message)
  },
}

const actions = {
  saveGroup(_, data) {
    return save(data)
  },

  setSubmitProgress({ commit }, isInProgress) {
    commit('SET_SUBMIT_PROGRESS', isInProgress)
  },

  getRootGroups({ commit }) {
    findAll().then(({ data }) =>
      commit('SET_CURRENT_GROUP', { children: data })
    )
  },

  setCurrentGroup({ commit }, groupId) {
    findById(groupId).then(({ data }) => {
      breadcrumb.reset()
      if (data.parents.length > 0) {
        breadcrumb.save([
          ...data.parents,
          {
            _id: data._id,
            name: data.name,
            level: data.parents.length,
          },
        ])
      }
      commit('SET_CURRENT_GROUP', data)
    })
  },

  setGroupToRemove({ commit }, group) {
    commit('SET_GROUP_TO_REMOVE', group)
  },

  setInactiveUsers({ commit }, group) {
    commit('SET_GROUP_INACTIVE_USERS', group)
  },

  removeGroup({ state }) {
    return removeGroup(state.groups.groupToRemove)
  },

  setGroupToEdit({ commit }, group) {
    findById(group._id).then(r => commit('SET_GROUP_TO_EDIT', r.data))
  },

  addToBreadcrumb({ commit }, group) {
    breadcrumb.add(group)
    commit('UPDATE_BREADCRUMB')
  },

  removeFromBreadcrumb({ commit }, group) {
    breadcrumb.remove(group)
    commit('UPDATE_BREADCRUMB')
  },

  resetPage({ commit }) {
    breadcrumb.reset()
    commit('RESET_STATE')
    actions.getRootGroups({ commit })
  },

  resetModal({ commit }) {
    commit('RESET_MODAL')
  },

  usersInactive({ state }) {
    return usersInactive(state.groups.groupInactiveUsers)
  },

  clearImportForm({ commit }) {
    commit('SET_IMPORT_FORM', importGroups())
  },

  updateImport({ commit }, form) {
    commit('SET_IMPORT_FORM', form)
  },

  async createGroups({ state, commit }, labels) {
    commit('SET_IMPORT_LOADING', true)
    commit('SET_IMPORT_LOADING_MESSAGE', labels.create.listing)

    const { data } = await getGroupsAccess()
    const groupsMap = data.reduce((map, group) => {
      map[group.name.toLowerCase()] = group._id
      return map
    }, {})

    let noErrors = true

    // create groups
    const groupsLength = state.import.groups.length
    await asyncForEach(state.import.groups, async (group, index) => {
      const name = group.label

      if (groupsMap[name.toLowerCase()]) {
        commit(
          'SET_IMPORT_LOADING_MESSAGE',
          `${labels.create.recover} (${index + 1}/${groupsLength}) : ${name}`
        )

        // @TODO try catch error
        const { data } = await findById(groupsMap[name.toLowerCase()])
        group.data = data
        return
      }

      commit(
        'SET_IMPORT_LOADING_MESSAGE',
        `${labels.create.creating} (${index + 1}/${groupsLength}) : ${name}`
      )

      const g = cleanGroupData()
      g.name = name
      g.inactive = true
      delete g.active

      // create group
      try {
        const { data } = await save(g)
        group.data = data
      } catch (e) {
        commit(
          'ADD_IMPORT_LOADING_ERROR',
          `${labels.create.error} "${g.name}" : ${e.response.data.name.message}`
        )
        noErrors = false
      }
    })

    // create subgroups
    await asyncForEach(state.import.groups, async (group, index) => {
      if (!group.children || group.children.length <= 0) {
        return
      }

      const label = `(${index + 1}/${groupsLength}) : ${group.label}`
      commit(
        'SET_IMPORT_LOADING_MESSAGE',
        `${labels.create.subgroups} ${label}`
      )

      const g = cleanGroupData()
      g.id = group.data._id
      g.name = group.label
      g.inactive = true
      g.members = group.data.members.map(m => m._id)
      g.managers = group.data.managers.map(m => m._id)
      g.children = group.children.map(c => c.data._id)
      delete g.active

      // update group
      try {
        const { data } = await save(g)
        group.data = data
      } catch (e) {
        const { children = {}, members = {}, managers = {} } = e.response.data
        commit(
          'ADD_IMPORT_LOADING_ERROR',
          `${labels.create.errorSubgroups} "${g.name}" : ${
            children.message ? children.message : ''
          } ${members.message ? members.message : ''} ${
            managers.message ? managers.message : ''
          }`
        )
        noErrors = false
      }
    })

    commit('SET_IMPORT_LOADING_MESSAGE', null)
    commit('SET_IMPORT_LOADING', false)
    return noErrors
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
